import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Alert } from '../components/ui/Alert';
import { Pagination } from '../components/ui/Pagination';
import { Sheet } from '../components/ui/Sheet';
import { ClientForm } from '../components/ClientForm';
import { apiFetch, parseJsonResponse, isNetworkError, isNetworkErrorResponse } from '../utils/api';

interface Client {
  id: number;
  account_name: string;
  telephone: string | null;
  code_client_license: number;
  email_license: string;
  export_id_hioffice: number;
  export_id_comments: number | null;
  dir_cloudclient: string | null;
  port_cloudclient: number | null;
  last_sync_date: string | null;
  date_export: string | null;
  is_active: number;
  created_at: string;
  updated_at: string;
}

export const Clients: React.FC = () => {
  const { token } = useAuth();
  const { can } = usePermissions();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const fetchClients = async () => {
    if (!token) return;
    
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });
      if (search) {
        params.append('search', search);
      }

      const route = `clients/list&${params}`;
      const res = await apiFetch(route, {}, token);
      
      // Si es error de red, mantener datos actuales y mostrar mensaje
      if (isNetworkErrorResponse(res)) {
        setError('Error de conexión. Verifica tu conexión a internet.');
        return;
      }

      const json = await parseJsonResponse(res);

      if (!res.ok || json.error) {
        throw new Error(json.error || json.message || json.data?.error || 'Error al cargar clientes');
      }

      setClients(json.data.clients || []);
      setPagination(json.data.pagination || pagination);
    } catch (err: any) {
      if (isNetworkError(err)) {
        setError('Error de conexión. Verifica tu conexión a internet.');
      } else {
      setError(err.message || 'Error inesperado');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchClients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pagination.page, search]);

  const handleCreate = () => {
    setEditingClient(null);
    setShowForm(true);
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!token) return;
    
    if (!window.confirm('¿Estás seguro de que deseas eliminar este cliente?')) {
      return;
    }

    try {
      const res = await apiFetch(`clients/delete&id=${id}`, { method: 'GET' }, token);
      
      // Si es error de red, mostrar mensaje pero no hacer nada más
      if (isNetworkErrorResponse(res)) {
        alert('Error de conexión. No se pudo eliminar el cliente.');
        return;
      }

      const json = await parseJsonResponse(res);

      if (!res.ok || json.error) {
        throw new Error(json.error || json.message || json.data?.error || 'Error al eliminar cliente');
      }

      fetchClients();
    } catch (err: any) {
      if (isNetworkError(err)) {
        alert('Error de conexión. Verifica tu conexión a internet.');
      } else {
      alert(err.message || 'Error al eliminar cliente');
      }
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingClient(null);
    fetchClients();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingClient(null);
  };

  const handleSearch = () => {
    setPagination({ ...pagination, page: 1 });
    fetchClients();
  };

  // Calcular estadísticas rápidas
  const activeClients = clients.filter(c => c.is_active === 1).length;
  const inactiveClients = clients.length - activeClients;

  return (
    <DashboardLayout
      title="Gestión de Clientes"
      subtitle="Administra los clientes del sistema"
      showBackButton
      backPath="/management"
    >
      {/* Sheet para formulario */}
      <Sheet
        isOpen={showForm}
        onClose={handleFormCancel}
        title={editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}
        subtitle={editingClient ? 'Modifica la información del cliente' : 'Completa los datos para crear un nuevo cliente'}
        size="lg"
      >
          <ClientForm
            client={editingClient}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
      </Sheet>

      <div className="space-y-6">
        {/* Estadísticas Rápidas */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-gray-600">Total de Clientes</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{pagination.total || clients.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-gray-600">Clientes Activos</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-600">{activeClients}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-gray-600">Clientes Inactivos</p>
                  <p className="text-xl sm:text-2xl font-bold text-red-600">{inactiveClients}</p>
                </div>
              </div>
            </CardContent>
        </Card>
        </div>

          {/* Actions Bar */}
            <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center justify-between">
                  <SearchBar
                    value={search}
                    onChange={setSearch}
                    onSubmit={handleSearch}
                placeholder="Buscar por nombre, email o código..."
                className="flex-1 w-full sm:w-auto min-w-0"
                  />
                  {can('clients.create') && (
                    <Button
                      variant="primary"
                      size="md"
                      onClick={handleCreate}
                      className="whitespace-nowrap w-full sm:w-auto"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <span className="hidden sm:inline">Nuevo Cliente</span>
                      <span className="sm:hidden">Nuevo</span>
                    </Button>
                  )}
                </div>
          </CardContent>
            </Card>

          {/* Error Message */}
          {error && (
              <Alert variant="error">{error}</Alert>
          )}

          {/* Clients Table */}
          {loading ? (
            <Card>
            <CardContent>
              <div className="p-12 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-4"></div>
                <p className="text-gray-600">Cargando clientes...</p>
              </div>
            </CardContent>
            </Card>
          ) : clients.length === 0 ? (
            <Card>
            <CardContent>
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay clientes registrados</h3>
                <p className="text-gray-600 mb-6">
                  {search ? 'No se encontraron clientes con los criterios de búsqueda.' : 'Comienza creando tu primer cliente.'}
                </p>
                {can('clients.create') && (
                  <Button variant="primary" onClick={handleCreate}>
                    Crear Primer Cliente
                  </Button>
                )}
              </div>
            </CardContent>
            </Card>
          ) : (
            <>
              {/* Vista de Cards para Mobile/Tablet - Similar a Tarifas */}
              <div className="block md:hidden space-y-4">
                {clients.map((client) => (
                  <Card key={client.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 mb-1 truncate">
                            {client.account_name}
                          </h4>
                          <p className="text-xs text-gray-500 truncate">
                            {client.email_license}
                          </p>
                        </div>
                        <Badge variant={client.is_active ? 'success' : 'error'} className="ml-2 flex-shrink-0">
                          {client.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Código:</span>
                          <span className="text-gray-900 font-mono text-xs">{client.code_client_license}</span>
                        </div>
                        {client.telephone && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Teléfono:</span>
                            <span className="text-gray-900">{client.telephone}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-gray-200">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(client)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(client.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Eliminar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                {/* Paginación para mobile/tablet */}
                <div className="pt-4">
                  <Pagination
                    page={pagination.page}
                    pages={pagination.pages}
                    total={pagination.total}
                    limit={pagination.limit}
                    onPageChange={(page) => setPagination({ ...pagination, page })}
                  />
                </div>
              </div>

              {/* Vista de Tabla para Desktop */}
              <Card className="hidden md:block">
                <div className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableHeaderCell>Nombre Comercial</TableHeaderCell>
                        <TableHeaderCell>Email</TableHeaderCell>
                        <TableHeaderCell>Código Licencia</TableHeaderCell>
                        <TableHeaderCell>Teléfono</TableHeaderCell>
                        <TableHeaderCell>Estado</TableHeaderCell>
                        <TableHeaderCell className="text-right">Acciones</TableHeaderCell>
                      </TableHeader>
                      <TableBody>
                        {clients.map((client) => (
                          <TableRow key={client.id}>
                            <TableCell>
                              <div className="font-semibold text-gray-900">{client.account_name}</div>
                            </TableCell>
                            <TableCell>
                              <div className="text-gray-600">{client.email_license}</div>
                            </TableCell>
                            <TableCell>
                              <div className="text-gray-600 font-mono text-xs">{client.code_client_license}</div>
                            </TableCell>
                            <TableCell>
                              <div className="text-gray-600">{client.telephone || '-'}</div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={client.is_active ? 'success' : 'error'}>
                                {client.is_active ? 'Activo' : 'Inactivo'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(client)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  Editar
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(client.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Eliminar
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="border-t border-gray-200 p-4">
                    <Pagination
                      page={pagination.page}
                      pages={pagination.pages}
                      total={pagination.total}
                      limit={pagination.limit}
                      onPageChange={(page) => setPagination({ ...pagination, page })}
                    />
                  </div>
                </div>
              </Card>
            </>
          )}
      </div>
    </DashboardLayout>
  );
};
